import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import produce from 'immer';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import logger from '../common/logger';
import { RETRY_COUNT, ROUTER_BASENAME } from '../common/constants';
import { getCourseAPIHost, getEnvironmentConstant } from '../common/utilities';
import { getEnrollmentData as uniGetEnrollmentData } from './universalDataFetchers';

axiosRetry(axios, { retries: RETRY_COUNT });

export const fetchEnrollmentData = createAsyncThunk(
  'enrollmentData',
  ({
    classId, env,
  }, { rejectWithValue }) => uniGetEnrollmentData({ params: { classId }, env, isPreview: false }).catch((err) => rejectWithValue(err)),
);

export const enrollInClass = createAsyncThunk(
  'enrollInClass',
  ({
    env, code, classId, userData, hash,
  },
  { rejectWithValue }) => {
    const host = getCourseAPIHost({ env, isPreview: false });
    const enrollmentUrl = `${host}${getEnvironmentConstant({ key: ROUTER_BASENAME, env })}/api/enroll/v1`;

    return axios.post(enrollmentUrl, {
      code,
      classId,
      hash,
      token: userData?.oa2Token,
    }).catch((err) => rejectWithValue(err));
  },
);

export const enrollmentSlice = createSlice({
  name: 'enrollmentData',
  initialState: {
    data: {},
    editorial: {},
    isLoaderVisible: true,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEnrollmentData.pending, (state) => ({
      ...state,
      isLoaderVisible: true,
    }));
    builder.addCase(fetchEnrollmentData.fulfilled, (state, action) => {
      const data = action?.payload;
      if (!data?.error) {
        const newState = produce(state, (draftState) => {
          if (draftState?.data?.id === data[0].id) { // Merge
            draftState.data = { ...draftState.data, ...data[0] };
          } else { // Overwrite
            // eslint-disable-next-line prefer-destructuring
            draftState.data = data[0];
          }
          // eslint-disable-next-line prefer-destructuring
          draftState.editorial = data[1];
          draftState.isLoaderVisible = false;
        });
        return newState;
      }

      return state;
    });
    builder.addCase(fetchEnrollmentData.rejected, (state, action) => {
      logger.error('Error in fetchEnrollmentData: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.hasError = true;
        draftState.isLoaderVisible = false;
      });

      return newState;
    });
    builder.addCase(enrollInClass.pending, (state) => {
      const newState = produce(state, (draftState) => {
        delete draftState.enrollmentError;
        draftState.enrolling = true;
      });

      return newState;
    });
    builder.addCase(enrollInClass.fulfilled, (state, action) => {
      const data = action?.payload;
      if (!data?.error) {
        const newState = produce(state, (draftState) => {
          // Overwrite but maintain data like id that isn't in Enroll API response
          draftState.data = { ...draftState.data, ...data.data?.classData };
          draftState.enrolling = false;
        });

        return newState;
      }

      return state;
    });
    builder.addCase(enrollInClass.rejected, (state, action) => {
      logger.error('Error in enrollInClass: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.enrollmentError = action?.payload?.response?.status === 401 ? 401 : 400;
        draftState.enrolling = false;
      });

      return newState;
    });
  },
});

// eslint-disable-next-line no-empty-pattern
export const {} = enrollmentSlice.actions;

export const getEnrollmentData = (state) => state.enrollmentData.data;
export const getEnrollmentError = (state) => state.enrollmentData.enrollmentError;
export const getEnrolling = (state) => state.enrollmentData.enrolling;
export const isLoaderVisible = (state) => state.enrollmentData.isLoaderVisible;
export const getEditorialData = (state) => state.enrollmentData.editorial;

export default enrollmentSlice.reducer;
